// Usage Tracking — the API usage counter, one branded infographic meter per navo24 service.
//
// UI-first / 0-filled: the allowances below are placeholders until the metering backend lands (per-org
// api_keys + air_shipment_meter / air_api_usage, mirroring trackingmcp's model). Each service reads in its
// brandbook §09 accent so a customer recognises it at a glance. Tracking + Air carry three metrics
// (unique / api-calls / validity); Schedules · Rates · Loading carry two (api-calls / validity).

import { Layout } from '../components/Layout'

interface Meter {
  label: string
  used: number
  limit: number | null // null = Pay-as-you-go → unlimited (running count, no bar)
}
// Validity = the plan window. A monthly/contract plan renews and eventually expires; a PAYG plan never
// caps, so its validity reads "unlimited".
type Validity =
  | { kind: 'period'; renews: string; daysLeft: number; contractEnds?: string }
  | { kind: 'payg' }
interface Service {
  name: string
  tagline?: string
  accent: string // brandbook §09 identity accent, tuned for the light content ground
  meters: Meter[]
  validity: Validity
}

// Brandbook §09 per-service accents (light-ground values). Cobalt = Tracking (master signal); Air = the
// sky-rim disc; Schedules = atlas teal; Rates = ledger red; Loading = engine orange.
const PERIOD: Validity = {
  kind: 'period',
  renews: '30 Aug 2026',
  daysLeft: 30,
  contractEnds: '31 Jul 2027',
}
const SERVICES: Service[] = [
  {
    name: 'Tracking',
    accent: '#1f4fe6',
    meters: [
      { label: 'Unique shipments', used: 0, limit: 1000 },
      { label: 'API calls', used: 0, limit: 100000 },
    ],
    validity: PERIOD,
  },
  {
    name: 'Air cargo',
    accent: '#2f6bff',
    meters: [
      { label: 'Unique shipments', used: 0, limit: 1000 },
      { label: 'API calls', used: 0, limit: 100000 },
    ],
    validity: PERIOD,
  },
  {
    name: 'Schedules',
    tagline: 'the atlas',
    accent: '#0e9e8f',
    meters: [{ label: 'API calls', used: 0, limit: 50000 }],
    validity: PERIOD,
  },
  {
    name: 'Freight rates',
    tagline: 'the ledger',
    accent: '#c0392b',
    meters: [{ label: 'API calls', used: 0, limit: 50000 }],
    validity: PERIOD,
  },
  {
    name: 'Loading',
    tagline: 'the engine',
    accent: '#d2591a',
    meters: [{ label: 'API calls', used: 0, limit: 50000 }],
    validity: PERIOD,
  },
]

const fmt = (n: number) => n.toLocaleString('en-US')

function UsageBlock({ svc }: { svc: Service }) {
  return (
    <section className="ut-card" style={{ ['--accent' as string]: svc.accent }}>
      <div className="ut-rail" />
      <div className="ut-body">
        <div className="ut-cardhead">
          <span className="ut-dot" />
          <span className="ut-title">{svc.name}</span>
          {svc.tagline && <span className="ut-tag">{svc.tagline}</span>}
          <span className="ut-scope">API</span>
        </div>
        <div className="ut-meters">
          {svc.meters.map((m) => {
            const unlimited = m.limit === null
            const pct =
              unlimited || m.limit === 0 ? 0 : Math.min(100, (m.used / (m.limit as number)) * 100)
            return (
              <div key={m.label}>
                <div className="ut-metertop">
                  <span className="ut-mlabel">{m.label}</span>
                  <span className="ut-mfig">
                    <span className="ut-used">{fmt(m.used)}</span>
                    <span className="ut-sep">/</span>
                    <span className="ut-lim">{unlimited ? '∞' : fmt(m.limit as number)}</span>
                  </span>
                </div>
                {unlimited ? (
                  <div className="ut-unlimited">Pay as you go · unlimited</div>
                ) : (
                  <div className="ut-track">
                    <div className="ut-fill" style={{ width: `${pct}%` }} />
                  </div>
                )}
              </div>
            )
          })}
          <div className="ut-validity">
            <span className="ut-mlabel">Validity</span>
            {svc.validity.kind === 'payg' ? (
              <span className="ut-renew">pay as you go · no expiry</span>
            ) : (
              <span className="ut-renew">
                renews {svc.validity.renews} · {svc.validity.daysLeft} days left
                {svc.validity.contractEnds ? ` · contract to ${svc.validity.contractEnds}` : ''}
              </span>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default function UsagePage() {
  return (
    <Layout>
      <style>{UT_CSS}</style>
      <div className="ut-wrap">
        <header className="ut-head">
          <div>
            <h1 className="ut-h1">Usage Tracking</h1>
            <p className="ut-sub">
              API consumption for the current billing period, per navo24 service. Web-widget usage
              is metered separately.
            </p>
          </div>
          <div className="ut-meta">
            <span className="ut-pill ut-plan">Free plan</span>
            <span className="ut-pill">1 – 30 Aug 2026</span>
          </div>
        </header>

        <div className="ut-grid">
          {SERVICES.map((s) => (
            <UsageBlock key={s.name} svc={s} />
          ))}
        </div>

        <div className="ut-foot">
          <span className="ut-k">unique ≠ shipments</span>
          <p>
            <b>Unique shipments</b> counts each reference once per billing period, and only when it
            triggered a fresh, billable lookup. Cache reads, re-opens, and shipments carried over
            from a prior period don’t add to it, so a workspace with 300 shipments can show a usage
            of 2. <b>API calls</b> counts every request, cache hits included.
          </p>
        </div>
      </div>
    </Layout>
  )
}

// Scoped styles (ut- prefix) built on the navo24 light-ground tokens from index.css so the page matches the
// rest of the site; per-service colour rides on the inline --accent set by each card.
const UT_CSS = `
.ut-wrap { max-width: 1040px; margin: 0 auto; padding: 48px 20px 72px; }
.ut-head { display: flex; flex-wrap: wrap; align-items: flex-end; justify-content: space-between; gap: 16px 24px; padding-bottom: 20px; border-bottom: 1px solid rgb(var(--border-rgb) / .1); }
.ut-h1 { font-family: 'Ranade', Georgia, serif; font-size: clamp(24px, 3.2vw, 30px); font-weight: 600; letter-spacing: -.01em; margin: 0 0 4px; color: rgb(var(--ink-rgb)); }
.ut-sub { margin: 0; color: rgb(var(--muted-rgb)); font-size: 14px; max-width: 54ch; line-height: 1.5; }
.ut-meta { display: flex; align-items: center; gap: 10px; }
.ut-pill { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 12px; letter-spacing: .02em; padding: 5px 10px; border: 1px solid rgb(var(--border-rgb) / .16); border-radius: 999px; color: rgb(var(--muted-rgb)); white-space: nowrap; }
.ut-plan { color: rgb(var(--ink-rgb)); border-color: rgb(var(--ink-rgb) / .45); }

.ut-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(320px, 1fr)); gap: 16px; margin-top: 24px; }
.ut-card { position: relative; background: rgb(var(--surface-0-rgb)); border: 1px solid rgb(var(--border-rgb) / .1); border-radius: 12px; box-shadow: var(--shadow); overflow: hidden; }
.ut-rail { height: 3px; width: 100%; background: var(--accent); }
.ut-body { padding: 18px 20px 20px; }
.ut-cardhead { display: flex; align-items: center; gap: 10px; }
.ut-dot { width: 9px; height: 9px; border-radius: 50%; background: var(--accent); flex: none; box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 16%, transparent); }
.ut-title { font-family: 'Ranade', Georgia, serif; font-size: 16px; font-weight: 600; letter-spacing: -.01em; color: rgb(var(--ink-rgb)); }
.ut-tag { color: rgb(var(--faint-rgb)); font-size: 13px; font-style: italic; }
.ut-scope { margin-left: auto; font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 10px; letter-spacing: .08em; text-transform: uppercase; color: rgb(var(--muted-rgb)); border: 1px solid rgb(var(--border-rgb) / .16); border-radius: 5px; padding: 2px 6px; }

.ut-meters { margin-top: 16px; display: flex; flex-direction: column; gap: 15px; }
.ut-metertop { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; margin-bottom: 6px; }
.ut-mlabel { font-size: 11px; letter-spacing: .07em; text-transform: uppercase; color: rgb(var(--muted-rgb)); font-weight: 500; }
.ut-mfig { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 13px; font-variant-numeric: tabular-nums; white-space: nowrap; }
.ut-used { color: var(--accent); font-weight: 600; }
.ut-sep { color: rgb(var(--faint-rgb)); margin: 0 3px; }
.ut-lim { color: rgb(var(--muted-rgb)); }
.ut-track { height: 7px; border-radius: 999px; background: rgb(var(--surface-2-rgb)); overflow: hidden; position: relative; }
.ut-fill { height: 100%; border-radius: 999px; background: var(--accent); transition: width .8s cubic-bezier(.2,.7,.3,1); }
.ut-track::after { content: ""; position: absolute; left: 0; top: 0; bottom: 0; width: 3px; border-radius: 999px; background: var(--accent); opacity: .35; }
.ut-unlimited { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: var(--accent); letter-spacing: .02em; }

.ut-validity { display: flex; align-items: baseline; justify-content: space-between; gap: 12px; padding-top: 4px; }
.ut-renew { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: rgb(var(--faint-rgb)); }

.ut-foot { margin-top: 26px; padding: 16px 18px; background: rgb(var(--surface-1-rgb)); border: 1px solid rgb(var(--border-rgb) / .1); border-radius: 10px; display: flex; gap: 12px; align-items: flex-start; }
.ut-k { font-family: 'JetBrains Mono', ui-monospace, monospace; font-size: 11px; color: rgb(var(--signal-rgb)); border: 1px solid rgb(var(--signal-rgb) / .4); border-radius: 5px; padding: 2px 7px; white-space: nowrap; margin-top: 1px; }
.ut-foot p { margin: 0; font-size: 13px; color: rgb(var(--muted-rgb)); line-height: 1.55; }
.ut-foot b { color: rgb(var(--ink-rgb)); font-weight: 600; }

@media (max-width: 560px) { .ut-head { flex-direction: column; align-items: flex-start; } }
`
